import { useState } from 'react';
import { useAnalytics, useSubscription } from '../api/hooks';
import type { Period } from '../api/types';
import Tile from '../components/Tile';
import { inr, inrShort, num } from '../components/money';
import { ErrorState, Loading } from '../components/States';

/** Switches, not a date picker: the API takes 1|7|30|90|365|0 and a picker that
 *  can emit 45 returns something the label does not describe (§10). */
const RANGES: { days: Period; label: string }[] = [
  { days: 1, label: '24h' }, { days: 7, label: '7d' }, { days: 30, label: '30d' },
];

export default function Dashboard() {
  const [days, setDays] = useState<Period>(1);
  const { data, isPending, error, refetch } = useAnalytics(days, null);
  const { data: sub } = useSubscription();

  if (isPending) return <Loading what="your dashboard" />;
  if (error) return <ErrorState error={error} onRetry={() => refetch()} />;

  const a = data!;
  const movedValue = a.consumption.value_trend.reduce((s, r) => s + r.value, 0);
  const movedSlips = a.consumption.trend.reduce((s, r) => s + r.slip_count, 0);
  const unpriced = a.value.unpriced_materials;
  const silent = a.accountability.stalled_sites ?? [];
  const atRisk = a.kpis.stockout_risk_count;
  const pending = a.kpis.pending_approvals;

  return (
    <>
      <div className="section__head">
        <h1 className="section__title">Dashboard</h1>
        <p className="section__sub muted">Everything below is {a.site}.</p>
        <div className="switches" role="group" aria-label="Time range">
          {RANGES.map((r) => (
            <button
              key={r.days}
              className={`switch${days === r.days ? ' switch--on' : ''}`}
              onClick={() => setDays(r.days)}
              aria-pressed={days === r.days}
            >{r.label}</button>
          ))}
        </div>
      </div>

      <div className="tiles">
        <Tile
          label="Awaiting your approval"
          value={num(pending.total)}
          tone={pending.total > 0 ? 'warn' : 'neutral'}
          hint={`${pending.slips} slips · ${pending.orders} orders`}
          action={pending.total > 0 ? 'Go and decide' : 'Nothing waiting'}
        />
        <Tile
          label={`Moved in the last ${days === 1 ? '24h' : `${days}d`}`}
          value={inrShort(movedValue)}
          hint={`${movedSlips} slip${movedSlips === 1 ? '' : 's'} approved`}
          action="Recognise a bad day the day it happens"
        />
        <Tile
          label="Stock at risk"
          value={num(atRisk)}
          tone={atRisk > 0 ? 'bad' : 'good'}
          hint="Out, or under 7 days' cover"
          action={atRisk > 0 ? 'Order it — or move it' : 'Nothing critical'}
        />
        <Tile
          label="Idle capital"
          value={inrShort(a.stock_intelligence.idle_stock_value)}
          tone={a.stock_intelligence.idle_stock_value > 0 ? 'warn' : 'neutral'}
          // The honesty field. A total over only some of the stock has to say so,
          // or a low number reads as good news rather than as missing prices.
          hint={a.stock_intelligence.idle_stock_unpriced > 0
            ? `${a.stock_intelligence.idle_stock_unpriced} material${a.stock_intelligence.idle_stock_unpriced === 1 ? '' : 's'} unpriced`
            : `${a.kpis.idle_stock_count} materials, 30+ days`}
          action="Redistribute it"
        />
        <Tile
          label="Silent sites"
          value={num(silent.length)}
          tone={silent.length > 0 ? 'warn' : 'good'}
          hint={silent.length ? silent.slice(0, 2).map((s) => s.site_name).join(', ') : 'All sites active'}
          action={silent.length > 0 ? 'Phone the manager' : undefined}
        />
        <Tile
          label="Plan"
          value={sub?.days_remaining !== null && sub?.days_remaining !== undefined ? `${sub.days_remaining}d` : '—'}
          tone={sub && sub.days_remaining !== null && sub.days_remaining <= 14 ? 'bad' : 'neutral'}
          hint={sub ? `${sub.plan} · ${sub.status}` : ''}
          action={sub && !sub.active ? 'Call us to renew' : undefined}
        />
      </div>

      {unpriced > 0 && (
        <p className="muted section__sub" style={{ marginTop: 'calc(-1 * var(--s-md))', marginBottom: 'var(--s-xl)' }}>
          {unpriced} material{unpriced === 1 ? ' has' : 's have'} no unit cost, so every rupee figure here is a
          floor, not a total.
        </p>
      )}

      <Redistribution data={a} />
    </>
  );
}

/**
 * §6.6 — idle stock, and where it should go. Not a graph: a ranked list of
 * concrete moves, and the reason the console is worth opening.
 *
 * v1 is ADVISORY. Executing a transfer writes inventory at two sites, and the
 * product's core invariant is that stock moves only through an approved slip.
 * Calling PUT /inventory/update twice would destroy stock at one site and conjure
 * it at another, with two unrelated audit rows and nothing linking them. So the
 * action here is "message the manager", not "transfer" (§2.2).
 */
function Redistribution({ data }: { data: import('../api/types').Analytics }) {
  const rows = data.stock_intelligence.transfer_suggestions ?? [];
  const idleByKey = new Map(
    data.stock_intelligence.idle_stock.map((i) => [`${i.site_name}||${i.material_name}`, i])
  );

  return (
    <section className="section">
      <div className="section__head">
        <h2 className="section__title">Idle stock, and where it should go</h2>
        <p className="section__sub muted">
          {inr(data.stock_intelligence.idle_stock_value)} of material has not moved in 30 days.
        </p>
      </div>

      {rows.length === 0 ? (
        <div className="card state">
          <div className="state__title">Nothing to move</div>
          <p className="muted" style={{ margin: 0 }}>
            No site is holding idle stock that another site is short of. That is the good case.
          </p>
        </div>
      ) : (
        <div className="card scroll-x" style={{ padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Material</th><th>From</th><th>Idle</th><th>Value</th>
                <th>To</th><th>Runs out</th><th>Move</th><th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const idle = idleByKey.get(`${r.from_site}||${r.material_name}`);
                return (
                  <tr key={`${r.material_name}-${r.from_site}-${i}`}>
                    <td><strong>{r.material_name}</strong></td>
                    <td>{r.from_site}</td>
                    <td className="mono-num muted">{idle ? `${idle.days_since_use}d` : '—'}</td>
                    <td className="mono-num">{inr(idle?.value ?? null)}</td>
                    <td>{r.to_site}</td>
                    <td className="mono-num">
                      {r.receiver_days_left === null || r.receiver_days_left === undefined
                        ? <span className="pill pill--bad">out</span>
                        : <span className={`pill ${r.receiver_days_left <= 7 ? 'pill--bad' : 'pill--warn'}`}>
                            {r.receiver_days_left}d
                          </span>}
                    </td>
                    <td className="mono-num"><strong>{num(r.suggested_transfer_qty)} {r.unit}</strong></td>
                    <td>
                      {/* Advisory only — see the note above this component. */}
                      <button className="btn btn--quiet" style={{ padding: '6px 10px', fontSize: 12 }}>
                        Message the manager
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
